import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppLayout from '../../../generic-components/Layout';
import BackIcon from '../../../assets/svg/backArrow.svg';
import { Preloader } from '../components/Preloader';
import {
  GENERATE_FORM_STEPS, NAVBAR_TITLE_MAP, POSSIBLE_FORM_TYPES, STYLES,
} from '../consts';
import { FlowManager } from '../components/FlowManager';
import { useGetFormsList } from '../hooks/useGetFormsList';
import { getFormNumber, serializeFormsList } from '../utils';
import { IFormList } from '../interfaces';
import { VIEWS_MAP } from '../../../router/constants';
import { useStore } from '../../../store';
import { Upgrade } from '../components/Upgrade';
import { trackMixPanel } from '../../../services/mixpanel.service';
import { canUpgradeToUseFeature, validateFeatureFlag } from '../../../services/featureFlagService';
import { FEATURE_FLAG } from '../../../constants/general';

const { APP_LAYOUT } = STYLES;

export const TaxForms = () => {
  const [formsList, setFormsList] = useState<IFormList>([]);
  const summaryData = useStore(state => state.summary);
  const { formType, generateStep } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const shouldShowUpgradeScreen = useCallback(() => {
    if (!formType) return false;
    const flagName = FEATURE_FLAG?.[formType as keyof typeof FEATURE_FLAG];
    return canUpgradeToUseFeature(flagName as string) && !validateFeatureFlag(flagName as string);
  }, [summaryData, formType]);

  const shouldSkipIntro = useMemo(() => formsList.some(item => item.fileCreated), [formsList]);

  const handleNavigate = () => {
    trackMixPanel(`Act on form ${getFormNumber(formType)}`, { Action: 'back' });
    if (generateStep === GENERATE_FORM_STEPS.CATEGORIZE) {
      if (shouldSkipIntro) {
        navigate(`/${VIEWS_MAP.DASHBOARD}`);
      } else {
        navigate(`/tax-forms/${formType}`);
      }
    } else if (GENERATE_FORM_STEPS.GENERATE && shouldSkipIntro) {
      navigate(`/${VIEWS_MAP.DASHBOARD}`);
    } else {
      navigate(`/tax-forms/${formType}`);
    }
  };

  /** Hooks */
  const { data: formsListResponse, isLoading } = useGetFormsList({ formType: formType || '' });

  const prevActiveYear = useMemo(() => formsListResponse?.prevActiveYear, [formsListResponse?.prevActiveYear]);

  useEffect(() => {
    if (!formsListResponse) return;
    const list = serializeFormsList(formsListResponse.data);
    if (!list) return;
    setFormsList(list);
  }, [formsListResponse]);

  useEffect(() => {
    if (!formType) {
      navigate(`/${VIEWS_MAP.DASHBOARD}`);
    } else if (formType && !POSSIBLE_FORM_TYPES.includes(formType)) {
      navigate(`/${VIEWS_MAP.DASHBOARD}`);
    }
  }, [formType]);

  return (
    <AppLayout
      assetPath={BackIcon}
      title={t(NAVBAR_TITLE_MAP[formType as keyof typeof NAVBAR_TITLE_MAP])}
      layoutStyle={APP_LAYOUT.STYLE}
      navBarStyle={`${APP_LAYOUT.NAVBAR} ${shouldShowUpgradeScreen() ? 'opacity-0' : ''}`}
      titleStyle={APP_LAYOUT.NAV_TITLE}
      backDestination={0}
      handleNavigate={handleNavigate}
      backButtonStyle={`${APP_LAYOUT.BACK_BUTTON} ${!generateStep ? 'hidden' : ''}`}
    >
      {shouldShowUpgradeScreen() ? <Upgrade /> : isLoading ? <Preloader /> : <FlowManager activeYear={prevActiveYear} list={formsList} /> }
    </AppLayout>
  );
};
